import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Plus, Minus, Heart, ShoppingCart, Flame, Footprints } from 'lucide-react'
import { customerApi } from '../services/api'
import { useCart } from '../contexts/CartContext'
import { useRestaurant } from '../contexts/RestaurantContext'
import { useLanguage } from '../contexts/LanguageContext'
import { ItemDetailShimmer } from '../components/Shimmer'
import AllergenIcons from '../components/AllergenIcons'
import { getLocalizedName, getLocalizedDescription } from '../utils/localization'

interface MenuItem {
  id: string
  name: string
  description: string
  base_price: number
  code?: string
  image?: string
  is_popular: boolean
  category_id?: string
  preparation_time?: number
  calories?: number
  steps_to_burn?: number
  allergens?: string[]
  variants?: Array<{
    id: string
    name: string
    priceModifier: number
    isDefault?: boolean
  }>
  addons?: Array<{
    id: string
    name: string
    price: number
    category?: string
    isRequired?: boolean
  }>
}

export default function ItemDetail() {
  const { itemId } = useParams<{ itemId: string }>()
  const navigate = useNavigate()
  const { addItem, items: cartItems } = useCart()
  const { formatPrice, allergenMap } = useRestaurant()
  const { t, language } = useLanguage()
  const [item, setItem] = useState<MenuItem | null>(null)
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedVariant, setSelectedVariant] = useState<string>('')
  const [selectedAddons, setSelectedAddons] = useState<string[]>([])
  const [quantity, setQuantity] = useState(1)
  const [addingToCart, setAddingToCart] = useState(false)

  useEffect(() => {
    if (itemId) loadItem()
  }, [itemId])

  // Scroll to top when opening product details so the full-width image is visible
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [itemId])

  const loadItem = async () => {
    try {
      setLoading(true)
      const response = await customerApi.getMenu()
      if (response.success && response.data && Array.isArray(response.data)) {
        const data = response.data as MenuItem[]
        setMenuItems(data)
        const foundItem = data.find((i: MenuItem) => i.id === itemId)
        if (foundItem) {
          const variants = Array.isArray(foundItem.variants) ? foundItem.variants : []
          setItem(foundItem)
          if (variants.length) {
            const defaultVariant = variants.find((v: { isDefault?: boolean }) => v.isDefault) || variants[0]
            setSelectedVariant(defaultVariant.name)
          }
        } else {
          navigate('/')
        }
      }
    } catch (error) {
      console.error('Error loading item:', error)
      navigate('/')
    } finally {
      setLoading(false)
    }
  }

  const toggleAddon = (addonName: string) => {
    setSelectedAddons(prev =>
      prev.includes(addonName) ? prev.filter(a => a !== addonName) : [...prev, addonName]
    )
  }

  const calculatePrice = () => {
    if (!item) return 0
    let price = item.base_price
    const variants = Array.isArray(item.variants) ? item.variants : []
    const addons = Array.isArray(item.addons) ? item.addons : []
    if (selectedVariant && variants.length) {
      const variant = variants.find(v => v.name === selectedVariant)
      if (variant) price += variant.priceModifier || 0
    }
    selectedAddons.forEach(addonName => {
      const addon = addons.find(a => a.name === addonName)
      if (addon) price += addon.price || 0
    })
    return price
  }

  const currentAddons = selectedAddons.length > 0 ? selectedAddons : undefined
  const isInCart = item && cartItems.some(
    i => i.menu_item_id === item.id &&
      i.variant_name === (selectedVariant || undefined) &&
      JSON.stringify(i.addons) === JSON.stringify(currentAddons)
  )

  const handleAddToCart = async () => {
    if (!item) return
    if (isInCart) {
      navigate('/cart')
      return
    }
    setAddingToCart(true)
    // Brief delay so user sees the three-dots animation
    await new Promise(r => setTimeout(r, 500))
    const variants = Array.isArray(item.variants) ? item.variants : []
    const addons = Array.isArray(item.addons) ? item.addons : []
    const variant = variants.find(v => v.name === selectedVariant)
    const addonsPrice = selectedAddons.reduce((sum, addonName) => {
      const addon = addons.find(a => a.name === addonName)
      return sum + (addon?.price || 0)
    }, 0)
    addItem({
      menu_item_id: item.id,
      name: getLocalizedName(item.name, (item as any).name_ar, language),
      base_price: item.base_price,
      variant_name: selectedVariant || undefined,
      variant_price_modifier: variant?.priceModifier || 0,
      addons: currentAddons,
      addons_price: addonsPrice,
      quantity,
      image: item.image,
      code: item.code
    })
    setAddingToCart(false)
  }

  if (loading) return <ItemDetailShimmer />
  if (!item) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Item not found</p>
          <Link to="/" className="text-orange-500 hover:underline">Go back to menu</Link>
        </div>
      </div>
    )
  }

  const unitPrice = calculatePrice()

  const sortByCode = (a: MenuItem, b: MenuItem) => {
    const codeA = a.code ?? ''
    const codeB = b.code ?? ''
    if (codeA !== codeB) return codeA.localeCompare(codeB, undefined, { numeric: true })
    return (a.name ?? '').localeCompare(b.name ?? '')
  }

  // Recommended sides: same category, exclude current, sort by code, limit 6
  const recommendedSides = menuItems
    .filter(m => m.id !== item.id && (item.category_id ? m.category_id === item.category_id : true))
    .sort(sortByCode)
    .slice(0, 6)

  // We also have: more from same category, excluding current and recommended sides, sort by code, limit 4
  const recommendedIds = new Set(recommendedSides.map(s => s.id))
  const weAlsoHave = menuItems
    .filter(
      m => m.id !== item.id &&
        !recommendedIds.has(m.id) &&
        (item.category_id ? m.category_id === item.category_id : true)
    )
    .sort(sortByCode)
    .slice(0, 4)

  // Allergen icons are shown below
  const itemAddons = Array.isArray(item.addons) ? item.addons : []
  const itemAllergens = Array.isArray(item.allergens) ? item.allergens : []

  const itemVariants = Array.isArray(item.variants) ? item.variants : []
  const itemDescription = getLocalizedDescription(item.description, (item as any).description_ar, language)?.trim() || ''

  return (
    <div className="min-h-screen bg-[#fafafa] pb-28">
      {/* Header: back, title, heart */}
      <header className="sticky top-0 z-30 bg-[#fafafa] border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5 rtl:scale-x-[-1]" />
          </button>
          <h1 className="text-base font-bold text-gray-800">{t('foodDetails')}</h1>
          <button type="button" className="p-2 text-gray-600 hover:text-gray-900" aria-label="Favorite">
            <Heart className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Full-width product image at top */}
      <div className="w-full aspect-[4/3] max-h-[320px] bg-gray-200 overflow-hidden">
        {item.image ? (
          <img src={item.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl">🍽</div>
        )}
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Product details - code, name, description, price - below image */}
        <div className="bg-gray-100 rounded-2xl p-4 mb-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              {item.code && (
                <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium font-mono bg-amber-200 text-amber-900">
                  {item.code}
                </span>
              )}
              <h2 className="text-xl font-bold text-gray-900">
                {getLocalizedName(item.name, (item as any).name_ar, language)}
              </h2>
            </div>
            {itemDescription && (
              <p className="text-sm text-gray-500 leading-relaxed">{itemDescription}</p>
            )}
            <p className="text-xl font-bold text-orange-500">{formatPrice(unitPrice)}</p>
          </div>

          {/* Nutritional info - Calories & Steps only, with icons and accent colors */}
          <div className="flex gap-3 mt-4">
            <div className="flex-1 flex items-center gap-3 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 py-3 px-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Flame className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] font-medium text-amber-700/80 uppercase tracking-wide">Calories</p>
                <p className="text-lg font-bold text-amber-900">{item.calories ?? '–'}</p>
              </div>
            </div>
            <div className="flex-1 flex items-center gap-3 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200/60 py-3 px-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <Footprints className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] font-medium text-emerald-700/80 uppercase tracking-wide">Steps to burn</p>
                <p className="text-lg font-bold text-emerald-900">{item.steps_to_burn ?? '–'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Variants */}
        {itemVariants.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">{t('size')}</p>
            <div className="flex flex-wrap gap-2">
              {itemVariants.map(v => (
                <button
                  key={v.id}
                  onClick={() => setSelectedVariant(v.name)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    selectedVariant === v.name ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {v.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Addons */}
        {itemAddons.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">{t('addons')}</p>
            <div className="space-y-2">
              {itemAddons.map(a => (
                <button
                  key={a.id}
                  onClick={() => toggleAddon(a.name)}
                  className={`w-full flex justify-between items-center px-4 py-2 rounded-lg text-sm ${
                    selectedAddons.includes(a.name)
                      ? 'bg-orange-100 text-orange-800 border border-orange-300'
                      : 'bg-gray-200 text-gray-800 border border-gray-300'
                  }`}
                >
                  <span>{a.name}</span>
                  {Number(a.price || 0) > 0 && <span>{formatPrice(a.price)}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Allergens (icons only; name on hover) */}
        {itemAllergens.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">{t('allergens')}</p>
            <AllergenIcons allergenIds={itemAllergens} allergenMap={allergenMap} size={24} />
          </div>
        )}

        {/* Recommended Sides */}
        {recommendedSides.length > 0 && (
          <div className="mb-4">
            <h3 className="text-base font-bold text-gray-900 mb-3">{t('recommendedSides')}</h3>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-4 px-4">
              {recommendedSides.map(side => (
                <Link
                  key={side.id}
                  to={`/item/${side.id}`}
                  className="shrink-0 w-40 rounded-xl bg-gray-100 overflow-hidden border border-gray-200 block"
                >
                  <div className="w-full h-28 bg-gray-200 overflow-hidden">
                    {side.image ? (
                      <img src={side.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">🍽</div>
                    )}
                  </div>
                  <div className="p-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {side.code && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium font-mono bg-amber-200 text-amber-900">
                          {side.code}
                        </span>
                      )}
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {getLocalizedName(side.name, (side as any).name_ar, language)}
                      </p>
                    </div>
                    <p className="text-xs font-bold text-orange-500 mt-0.5">{formatPrice(side.base_price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* We also have: more from same category */}
        {weAlsoHave.length > 0 && (
          <div className="mb-4">
            <h3 className="text-base font-bold text-gray-900 mb-3">{t('weAlsoHave')}</h3>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-4 px-4">
              {weAlsoHave.map(product => (
                <Link
                  key={product.id}
                  to={`/item/${product.id}`}
                  className="shrink-0 w-40 rounded-xl bg-gray-100 overflow-hidden border border-gray-200 block"
                >
                  <div className="w-full h-28 bg-gray-200 overflow-hidden">
                    {product.image ? (
                      <img src={product.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">🍽</div>
                    )}
                  </div>
                  <div className="p-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {product.code && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium font-mono bg-amber-200 text-amber-900">
                          {product.code}
                        </span>
                      )}
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {getLocalizedName(product.name, (product as any).name_ar, language)}
                      </p>
                    </div>
                    <p className="text-xs font-bold text-orange-500 mt-0.5">{formatPrice(product.base_price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom bar: quantity + ADD TO ORDER - always shown so users can add to cart */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-gray-100 border-t border-gray-200 safe-area-pb">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
            <div className="flex items-center rounded-xl bg-white border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-2.5 text-gray-600 hover:bg-gray-50"
              >
                <Minus className="w-5 h-5" />
              </button>
              <span className="w-10 text-center font-semibold text-gray-900">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity(quantity + 1)}
                className="p-2.5 text-gray-600 hover:bg-gray-50"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={addingToCart}
              className={`flex-1 py-3.5 rounded-xl font-bold text-sm uppercase transition-colors flex items-center justify-center gap-2 min-h-[48px] ${
                isInCart
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-90 disabled:cursor-wait'
              }`}
            >
              {addingToCart ? (
                <span className="flex items-center gap-1" aria-label="Adding">
                  <span className="dot-bounce w-2 h-2 rounded-full bg-white" />
                  <span className="dot-bounce w-2 h-2 rounded-full bg-white animation-delay-150" />
                  <span className="dot-bounce w-2 h-2 rounded-full bg-white animation-delay-300" />
                </span>
              ) : isInCart ? (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  {t('viewCart')}
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  {t('addToOrder')}
                </>
              )}
            </button>
          </div>
        </div>
    </div>
  )
}
